import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#c48f8b',
      light: '#d9b0ac',
      dark: '#a67470',
      contrastText: '#fff8f7',
    },
    secondary: {
      main: '#8C8BC4',
      light: '#aba9d8',
      dark: '#6968a8',
      contrastText: '#f8f8ff',
    },
    background: {
      default: '#fdf8f5',
      paper: '#fefcfa',
    },
    text: {
      primary: '#3a2b2b',
      secondary: '#7a6060',
      disabled: 'rgba(58,43,43,0.38)',
    },
    divider: '#e8d5d1',
    success: { main: '#7cb98a' },
    error: { main: '#c46a6a' },
  },

  typography: {
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 600,
      color: '#c48f8b',
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 600,
      color: '#8C8BC4',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 600,
      color: '#c48f8b',
    },
    h4: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 600,
      color: '#8C8BC4',
    },
    h5: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 600,
      color: '#3a2b2b',
    },
    h6: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 500,
      color: '#3a2b2b',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.65,
      color: '#3a2b2b',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#7a6060',
    },
    subtitle1: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.15rem',
      fontStyle: 'italic',
      color: '#7a6060',
    },
    button: {
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
  },

  shape: {
    borderRadius: 16,
  },

  shadows: [
    'none',
    '0 1px 4px rgba(196,143,139,0.08)',
    '0 2px 8px rgba(196,143,139,0.10)',
    '0 4px 16px rgba(196,143,139,0.12)',
    '0 6px 24px rgba(196,143,139,0.14)',
    '0 8px 32px rgba(196,143,139,0.16)',
    '0 10px 40px rgba(196,143,139,0.18)',
    '0 12px 48px rgba(196,143,139,0.20)',
    '0 14px 56px rgba(196,143,139,0.22)',
    '0 16px 64px rgba(196,143,139,0.24)',
    '0 18px 72px rgba(196,143,139,0.24)',
    '0 20px 80px rgba(196,143,139,0.24)',
    '0 22px 88px rgba(196,143,139,0.24)',
    '0 24px 96px rgba(196,143,139,0.24)',
    '0 26px 104px rgba(196,143,139,0.24)',
    '0 28px 112px rgba(196,143,139,0.24)',
    '0 30px 120px rgba(196,143,139,0.24)',
    '0 32px 128px rgba(196,143,139,0.24)',
    '0 34px 136px rgba(196,143,139,0.24)',
    '0 36px 144px rgba(196,143,139,0.24)',
    '0 38px 152px rgba(196,143,139,0.24)',
    '0 40px 160px rgba(196,143,139,0.24)',
    '0 42px 168px rgba(196,143,139,0.24)',
    '0 44px 176px rgba(196,143,139,0.24)',
    '0 46px 184px rgba(196,143,139,0.24)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#fdf8f5',
          color: '#3a2b2b',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(196,143,139,0.12)',
          border: '1px solid rgba(232,213,209,0.5)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(196,143,139,0.22)',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 50,
          padding: '9px 22px',
          fontWeight: 500,
          letterSpacing: '0.02em',
          transition: 'all 0.25s ease',
        },
        contained: {
          boxShadow: '0 3px 12px rgba(196,143,139,0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(196,143,139,0.35)',
          },
        },
        containedInherit: {
          backgroundColor: '#a09a9a',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#8e8888',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          fontWeight: 500,
          fontFamily: "'DM Sans', sans-serif",
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': { borderColor: '#e8d5d1' },
            '&:hover fieldset': { borderColor: '#c48f8b' },
            '&.Mui-focused fieldset': { borderColor: '#c48f8b' },
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 16px rgba(196,143,139,0.15)',
        },
      },
    },
  },
});

export default theme;
